import * as THREE from 'three';
import type { Preset, QualityName } from './presets';
import { QUALITY } from './presets';
import {
  fullscreenVertex, particleFragment, particleVertex, simulationFragment,
  trailFragment, trailHistoryFragment, trailVertex, velocitySampleFragment
} from './shaders';

const MAX_MASSES = 8;
const QUEST_TIME_SCALE = 0.04;
const TRAIL_LENGTH = 32;
const TRACER_COUNT = 512;
const AUDIO_SAMPLE_GRID = 64;

export interface AudioHistograms {
  velocity: Float32Array;
  distance: Float32Array;
}

export class Simulation {
  readonly root = new THREE.Group();
  private readonly renderer: THREE.WebGLRenderer;
  private readonly computeScene = new THREE.Scene();
  private readonly computeCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly computeQuad: THREE.Mesh;
  private readonly simulationMaterial: THREE.RawShaderMaterial;
  private readonly particleMaterial: THREE.ShaderMaterial;
  private readonly trailScene = new THREE.Scene();
  private readonly trailMaterial: THREE.RawShaderMaterial;
  private readonly trailQuad: THREE.Mesh;
  private readonly gravityMarkers = new THREE.Group();
  private readonly velocitySampleScene = new THREE.Scene();
  private readonly velocitySampleMaterial: THREE.RawShaderMaterial;
  private readonly velocitySampleTarget: THREE.WebGLRenderTarget;
  private readonly velocitySamplePixels = new Uint8Array(AUDIO_SAMPLE_GRID * AUDIO_SAMPLE_GRID * 4);
  private stateTargets: THREE.WebGLRenderTarget[] = [];
  private trailTargets: THREE.WebGLRenderTarget[] = [];
  private trailTargetIndex = 0;
  private trailValidColumns = 0;
  private currentPosition!: THREE.Texture;
  private currentVelocity!: THREE.Texture;
  private nextStateTarget = 0;
  private points?: THREE.Points;
  private trails?: THREE.LineSegments;
  private initialTextures: THREE.DataTexture[] = [];
  private preset!: Preset;
  private quality!: QualityName;
  private size = 0;
  private sizeOverride?: number;
  private baseVisualScale = 1;
  private structureScale = 1;
  private trailsEnabled = false;
  private gravityMarkersEnabled = false;

  paused = false;

  constructor(renderer: THREE.WebGLRenderer, preset: Preset, quality: QualityName, sizeOverride?: number) {
    this.renderer = renderer;
    this.sizeOverride = sizeOverride;
    if (!renderer.capabilities.isWebGL2) throw new Error('GSpace requires WebGL 2.');
    if (!renderer.extensions.has('EXT_color_buffer_float')) {
      throw new Error('Floating-point simulation buffers are not supported on this device.');
    }

    const masses = Array.from({ length: MAX_MASSES }, () => new THREE.Vector3());
    const common = {
      positionTexture: { value: null as THREE.Texture | null },
      velocityTexture: { value: null as THREE.Texture | null },
      pointMasses: { value: masses },
      pointMassCount: { value: 1 },
      gravityMass: { value: 1 },
      stepScale: { value: 1 },
      cullDistance: { value: 2000 },
      substepCount: { value: 1 }
    };
    this.simulationMaterial = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: fullscreenVertex,
      fragmentShader: simulationFragment,
      uniforms: THREE.UniformsUtils.clone(common),
      depthTest: false,
      depthWrite: false
    });
    this.computeQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.simulationMaterial);
    this.computeQuad.frustumCulled = false;
    this.computeScene.add(this.computeQuad);

    this.particleMaterial = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: particleVertex,
      fragmentShader: particleFragment,
      uniforms: {
        positionTexture: { value: null },
        colourScale: { value: 5 },
        pointScale: { value: 10 },
        distantBrightness: { value: 1 },
        structureScale: { value: 1 }
      },
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthTest: false,
      depthWrite: false
    });
    this.trailMaterial = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: fullscreenVertex,
      fragmentShader: trailHistoryFragment,
      uniforms: {
        positionTexture: { value: null },
        historyTexture: { value: null },
        particleGrid: { value: 1 },
        tracerCount: { value: TRACER_COUNT }
      },
      depthTest: false,
      depthWrite: false
    });
    this.trailQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.trailMaterial);
    this.trailQuad.frustumCulled = false;
    this.trailScene.add(this.trailQuad);
    this.velocitySampleMaterial = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: fullscreenVertex,
      fragmentShader: velocitySampleFragment,
      uniforms: {
        velocityTexture: { value: null },
        positionTexture: { value: null },
        particleGrid: { value: 1 },
        velocityRange: { value: 25 },
        distanceRange: { value: 1000 },
        pointMasses: { value: Array.from({ length: MAX_MASSES }, () => new THREE.Vector3()) },
        pointMassCount: { value: 1 }
      },
      depthTest: false,
      depthWrite: false
    });
    const velocitySampleQuad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.velocitySampleMaterial);
    velocitySampleQuad.frustumCulled = false;
    this.velocitySampleScene.add(velocitySampleQuad);
    this.velocitySampleTarget = new THREE.WebGLRenderTarget(AUDIO_SAMPLE_GRID, AUDIO_SAMPLE_GRID, {
      type: THREE.UnsignedByteType,
      format: THREE.RGBAFormat,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      depthBuffer: false,
      stencilBuffer: false
    });
    this.gravityMarkers.name = 'gravity-source-markers';
    this.root.add(this.gravityMarkers);
    this.root.position.set(0, 1.55, -2.6);
    this.rebuild(preset, quality);
  }

  rebuild(preset: Preset, quality: QualityName, sizeOverride = this.sizeOverride): void {
    this.preset = preset;
    this.quality = quality;
    this.sizeOverride = sizeOverride;
    this.size = THREE.MathUtils.clamp(Math.round(sizeOverride ?? QUALITY[quality].size), 64, 1224);
    const visualSpan = Math.max(preset.radius + Math.abs(preset.offsetX), preset.displacement * 2, 150);
    this.baseVisualScale = THREE.MathUtils.clamp(1.3 / visualSpan, 0.00065, 0.01);
    this.applyStructureScale();
    this.disposeState();

    const positionData = new Float32Array(this.size * this.size * 4);
    const velocityData = new Float32Array(this.size * this.size * 4);
    const initialMasses = this.makePointMasses(preset);
    const velocity = new THREE.Vector3();
    for (let i = 0; i < this.size * this.size; i++) {
      const j = i * 4;
      const point = this.createPoint(preset);
      point.y -= preset.offsetY;
      point.z -= preset.offsetX;
      this.createVelocity(preset, point, velocity);
      if (preset.legacyMotionTuned) this.applyLegacyMotionTuning(preset, point, velocity, initialMasses);
      positionData[j] = point.x;
      positionData[j + 1] = point.y;
      positionData[j + 2] = point.z;
      positionData[j + 3] = velocity.length();
      velocityData[j] = velocity.x;
      velocityData[j + 1] = velocity.y;
      velocityData[j + 2] = velocity.z;
      velocityData[j + 3] = 1;
    }

    const positionTexture = this.dataTexture(positionData);
    const velocityTexture = this.dataTexture(velocityData);
    this.initialTextures.push(positionTexture, velocityTexture);
    this.currentPosition = positionTexture;
    this.currentVelocity = velocityTexture;
    this.stateTargets = [this.target(), this.target()];
    this.nextStateTarget = 0;

    const particleUv = new Float32Array(this.size * this.size * 2);
    const dummyPositions = new Float32Array(this.size * this.size * 3);
    let cursor = 0;
    for (let y = 0; y < this.size; y++) {
      for (let x = 0; x < this.size; x++) {
        particleUv[cursor++] = (x + 0.5) / this.size;
        particleUv[cursor++] = (y + 0.5) / this.size;
      }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(dummyPositions, 3));
    geometry.setAttribute('particleUv', new THREE.BufferAttribute(particleUv, 2));
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 100000);
    this.points = new THREE.Points(geometry, this.particleMaterial);
    this.points.frustumCulled = false;
    this.root.add(this.points);
    this.createTrailResources();
    this.applyPresetUniforms();
    this.updateParticleTextures();
    this.paused = false;
  }

  step(iterations = 1, force = false): void {
    if (this.paused && !force) return;
    this.simulationMaterial.uniforms.substepCount.value = THREE.MathUtils.clamp(Math.floor(iterations), 1, 20);
    const oldTarget = this.renderer.getRenderTarget();
    const oldAutoClear = this.renderer.autoClear;
    const oldXrEnabled = this.renderer.xr.enabled;
    // Off-screen simulation passes must use their orthographic camera, not
    // WebXRManager's stereo camera. The XR framebuffer is restored afterward.
    this.renderer.xr.enabled = false;
    this.renderer.autoClear = true;
    try {
      this.setComputeTextures(this.simulationMaterial);
      const stateTarget = this.stateTargets[this.nextStateTarget];
      this.renderer.setRenderTarget(stateTarget);
      this.renderer.render(this.computeScene, this.computeCamera);
      this.currentPosition = stateTarget.textures[0];
      this.currentVelocity = stateTarget.textures[1];
      this.nextStateTarget = 1 - this.nextStateTarget;
      if (this.trailsEnabled) this.updateTrailHistory();
    } finally {
      this.renderer.setRenderTarget(oldTarget);
      this.renderer.autoClear = oldAutoClear;
      this.renderer.xr.enabled = oldXrEnabled;
    }
    this.updateParticleTextures();
  }

  setColourScale(value: number): void {
    this.particleMaterial.uniforms.colourScale.value = THREE.MathUtils.clamp(value, 1, 100);
  }

  setPointScale(value: number): void {
    this.particleMaterial.uniforms.pointScale.value = THREE.MathUtils.clamp(value, 2, 20);
  }

  setStructureScale(value: number): void {
    this.structureScale = THREE.MathUtils.clamp(value, 0.5, 40);
    this.particleMaterial.uniforms.structureScale.value = this.structureScale;
    this.applyStructureScale();
    this.applyGravityMarkerScale();
  }

  setDistantBrightness(value: number): void {
    this.particleMaterial.uniforms.distantBrightness.value = THREE.MathUtils.clamp(value, 1, 6);
  }

  setTracerTrails(enabled: boolean): void {
    this.trailsEnabled = enabled;
    if (this.trails) this.trails.visible = enabled;
    if (enabled) {
      this.trailValidColumns = 0;
      this.trailTargetIndex = 0;
      this.updateTrailDisplayTexture();
    }
  }

  setGravityMarkers(enabled: boolean): void {
    this.gravityMarkersEnabled = enabled;
    this.gravityMarkers.visible = enabled;
  }

  getGravitySourceWorldPositions(): THREE.Vector3[] {
    this.root.updateMatrixWorld(true);
    return this.gravityMarkers.children.map((marker) => marker.getWorldPosition(new THREE.Vector3()));
  }

  setCulling(enabled: boolean, distance: number): void {
    this.simulationMaterial.uniforms.cullDistance.value = enabled
      ? THREE.MathUtils.clamp(distance, 100, 50000)
      : 1e12;
  }

  sampleAudioHistograms(velocityRange: number, distanceRange: number): AudioHistograms {
    const velocity = new Float32Array(8);
    const distance = new Float32Array(8);
    const oldTarget = this.renderer.getRenderTarget();
    const oldAutoClear = this.renderer.autoClear;
    const oldXrEnabled = this.renderer.xr.enabled;
    this.renderer.xr.enabled = false;
    this.renderer.autoClear = true;
    try {
      this.velocitySampleMaterial.uniforms.velocityTexture.value = this.currentVelocity;
      this.velocitySampleMaterial.uniforms.positionTexture.value = this.currentPosition;
      this.velocitySampleMaterial.uniforms.particleGrid.value = this.size;
      this.velocitySampleMaterial.uniforms.velocityRange.value = THREE.MathUtils.clamp(velocityRange, 1, 100);
      this.velocitySampleMaterial.uniforms.distanceRange.value = THREE.MathUtils.clamp(distanceRange, 10, 50000);
      this.renderer.setRenderTarget(this.velocitySampleTarget);
      this.renderer.render(this.velocitySampleScene, this.computeCamera);
      this.renderer.readRenderTargetPixels(
        this.velocitySampleTarget, 0, 0, AUDIO_SAMPLE_GRID, AUDIO_SAMPLE_GRID, this.velocitySamplePixels
      );
    } finally {
      this.renderer.setRenderTarget(oldTarget);
      this.renderer.autoClear = oldAutoClear;
      this.renderer.xr.enabled = oldXrEnabled;
    }
    const sampleCount = AUDIO_SAMPLE_GRID * AUDIO_SAMPLE_GRID;
    for (let pixel = 0; pixel < sampleCount; pixel++) {
      const encodedSpeed = this.velocitySamplePixels[pixel * 4] / 255;
      const encodedDistance = this.velocitySamplePixels[pixel * 4 + 1] / 255;
      velocity[Math.min(Math.floor(encodedSpeed * 8), 7)]++;
      distance[Math.min(Math.floor(encodedDistance * 8), 7)]++;
    }
    for (let band = 0; band < velocity.length; band++) {
      velocity[band] /= sampleCount;
      distance[band] /= sampleCount;
    }
    return { velocity, distance };
  }

  private setComputeTextures(material: THREE.RawShaderMaterial): void {
    material.uniforms.positionTexture.value = this.currentPosition;
    material.uniforms.velocityTexture.value = this.currentVelocity;
  }

  private updateParticleTextures(): void {
    this.particleMaterial.uniforms.positionTexture.value = this.currentPosition;
  }

  private updateTrailHistory(): void {
    if (this.trailTargets.length !== 2) return;
    const source = this.trailTargets[this.trailTargetIndex];
    const destinationIndex = 1 - this.trailTargetIndex;
    const destination = this.trailTargets[destinationIndex];
    this.trailMaterial.uniforms.positionTexture.value = this.currentPosition;
    this.trailMaterial.uniforms.historyTexture.value = source.texture;
    this.trailMaterial.uniforms.particleGrid.value = this.size;
    this.renderer.setRenderTarget(destination);
    this.renderer.render(this.trailScene, this.computeCamera);
    this.trailTargetIndex = destinationIndex;
    this.trailValidColumns = Math.min(this.trailValidColumns + 1, TRAIL_LENGTH);
    this.updateTrailDisplayTexture();
  }

  private updateTrailDisplayTexture(): void {
    if (!this.trails || this.trailTargets.length !== 2) return;
    const material = this.trails.material as THREE.ShaderMaterial;
    material.uniforms.trailTexture.value = this.trailTargets[this.trailTargetIndex].texture;
    material.uniforms.validColumns.value = this.trailValidColumns;
  }

  private createTrailResources(): void {
    this.trailTargets = [this.trailTarget(), this.trailTarget()];
    this.trailTargetIndex = 0;
    this.trailValidColumns = 0;

    const vertexCount = TRACER_COUNT * (TRAIL_LENGTH - 1) * 2;
    const dummyPositions = new Float32Array(vertexCount * 3);
    const trailUvs = new Float32Array(vertexCount * 2);
    const otherUvs = new Float32Array(vertexCount * 2);
    const ages = new Float32Array(vertexCount);
    const segments = new Float32Array(vertexCount);
    let vertex = 0;
    for (let tracer = 0; tracer < TRACER_COUNT; tracer++) {
      const y = (tracer + 0.5) / TRACER_COUNT;
      for (let segment = 0; segment < TRAIL_LENGTH - 1; segment++) {
        const x0 = (segment + 0.5) / TRAIL_LENGTH;
        const x1 = (segment + 1.5) / TRAIL_LENGTH;
        const age = segment / (TRAIL_LENGTH - 1);
        trailUvs.set([x0, y, x1, y], vertex * 2);
        otherUvs.set([x1, y, x0, y], vertex * 2);
        ages.set([age, age], vertex);
        segments.set([segment, segment], vertex);
        vertex += 2;
      }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(dummyPositions, 3));
    geometry.setAttribute('trailUv', new THREE.BufferAttribute(trailUvs, 2));
    geometry.setAttribute('trailOtherUv', new THREE.BufferAttribute(otherUvs, 2));
    geometry.setAttribute('trailAge', new THREE.BufferAttribute(ages, 1));
    geometry.setAttribute('trailSegment', new THREE.BufferAttribute(segments, 1));
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 100000);
    const material = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: trailVertex,
      fragmentShader: trailFragment,
      uniforms: {
        trailTexture: { value: this.trailTargets[0].texture },
        validColumns: { value: 0 }
      },
      blending: THREE.AdditiveBlending,
      transparent: true,
      depthTest: false,
      depthWrite: false
    });
    this.trails = new THREE.LineSegments(geometry, material);
    this.trails.frustumCulled = false;
    this.trails.visible = this.trailsEnabled;
    this.root.add(this.trails);
  }

  private trailTarget(): THREE.WebGLRenderTarget {
    return new THREE.WebGLRenderTarget(TRAIL_LENGTH, TRACER_COUNT, {
      type: THREE.FloatType,
      format: THREE.RGBAFormat,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      wrapS: THREE.ClampToEdgeWrapping,
      wrapT: THREE.ClampToEdgeWrapping,
      depthBuffer: false,
      stencilBuffer: false
    });
  }

  private applyStructureScale(): void {
    this.root.scale.setScalar(this.baseVisualScale * this.structureScale);
  }

  private applyPresetUniforms(): void {
    const masses = this.makePointMasses(this.preset);
    this.rebuildGravityMarkers(masses);
    this.simulationMaterial.uniforms.gravityMass.value = this.preset.gravity;
    this.simulationMaterial.uniforms.stepScale.value = QUEST_TIME_SCALE;
    this.simulationMaterial.uniforms.cullDistance.value = Math.max(this.preset.radius * 8, 6000);
    this.simulationMaterial.uniforms.pointMassCount.value = masses.length;
    const values = this.simulationMaterial.uniforms.pointMasses.value as THREE.Vector3[];
    values.forEach((value, index) => value.copy(masses[index] ?? masses[0]));
    this.velocitySampleMaterial.uniforms.pointMassCount.value = masses.length;
    const sampleValues = this.velocitySampleMaterial.uniforms.pointMasses.value as THREE.Vector3[];
    sampleValues.forEach((value, index) => value.copy(masses[index] ?? masses[0]));
    this.setColourScale(this.preset.colourScale);
    this.setPointScale(this.quality === 'performance' ? 8 : 10);
  }

  private rebuildGravityMarkers(masses: THREE.Vector3[]): void {
    for (const child of [...this.gravityMarkers.children]) {
      this.gravityMarkers.remove(child);
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        const material = child.material;
        if (Array.isArray(material)) material.forEach((item) => item.dispose());
        else material.dispose();
      }
    }
    const visualSpan = Math.max(
      this.preset.radius + Math.abs(this.preset.offsetX),
      this.preset.displacement * 2,
      150
    );
    const markerRadius = visualSpan * 0.018;
    masses.forEach((mass, index) => {
      const marker = new THREE.Mesh(
        new THREE.SphereGeometry(markerRadius, 12, 8),
        new THREE.MeshBasicMaterial({
          color: index % 2 === 0 ? 0xffd36a : 0xff8ac8,
          transparent: true,
          opacity: 0.92,
          blending: THREE.AdditiveBlending,
          depthTest: false,
          depthWrite: false
        })
      );
      marker.position.copy(mass);
      this.gravityMarkers.add(marker);
    });
    this.applyGravityMarkerScale();
    this.gravityMarkers.visible = this.gravityMarkersEnabled;
  }

  private applyGravityMarkerScale(): void {
    const inverseStructureScale = 1 / this.structureScale;
    for (const marker of this.gravityMarkers.children) marker.scale.setScalar(inverseStructureScale);
  }

  private makePointMasses(preset: Preset): THREE.Vector3[] {
    const d = preset.displacement;
    switch (preset.massShape) {
      case 'single': return [new THREE.Vector3(0, 0, d)];
      case 'symmetricDouble': return [new THREE.Vector3(-d, 0, 0), new THREE.Vector3(d, 0, 0)];
      case 'double': return preset.particleShape === 'sphere'
        ? [new THREE.Vector3(0, 0, d), new THREE.Vector3(0, 0, -d)]
        : [new THREE.Vector3(0, 0, d), new THREE.Vector3(0, 0, 2 * d)];
      case 'quad': return preset.particleShape === 'sphere'
        ? [-d, -2 * d, d, 2 * d].map((z) => new THREE.Vector3(0, 0, z))
        : [d, 2 * d, 3 * d, 4 * d].map((z) => new THREE.Vector3(0, 0, z));
      case 'star': return [
        new THREE.Vector3(0, 0, -d), new THREE.Vector3(0, 0, d),
        new THREE.Vector3(0, d, 0), new THREE.Vector3(0, -d, 0),
        new THREE.Vector3(-d, 0, 0), new THREE.Vector3(d, 0, 0)
      ];
      case 'cube':
      default:
        return [-1, 1].flatMap((x) => [-1, 1].flatMap((y) => [-1, 1].map((z) => new THREE.Vector3(x * d, y * d, z * d))));
    }
  }

  private createPoint(preset: Preset): THREE.Vector3 {
    const radius = preset.radius;
    if (preset.particleShape === 'ring') {
      const r = radius * (0.72 + Math.random() * 0.28);
      const angle = Math.random() * Math.PI * 2;
      return new THREE.Vector3(Math.cos(angle) * r, Math.sin(angle) * r, (Math.random() - 0.5) * radius * 0.05);
    }
    if (preset.particleShape === 'doubleSphere') {
      const z = Math.random() * 2 - 1;
      const angle = Math.random() * Math.PI * 2;
      const radial = Math.sqrt(1 - z * z);
      const r = Math.cbrt(Math.random()) * radius;
      const side = Math.random() < 0.5 ? -1 : 1;
      return new THREE.Vector3(side * radius * 2.2 + radial * Math.cos(angle) * r, radial * Math.sin(angle) * r, z * r);
    }
    if (preset.particleShape === 'disk') {
      const r = Math.sqrt(Math.random()) * radius;
      const angle = Math.random() * Math.PI * 2;
      return new THREE.Vector3(Math.cos(angle) * r, Math.sin(angle) * r, 0);
    }
    const z = Math.random() * 2 - 1;
    const angle = Math.random() * Math.PI * 2;
    const radial = Math.sqrt(1 - z * z);
    const r = preset.particleShape === 'solidSphere' ? Math.cbrt(Math.random()) * radius : radius;
    return new THREE.Vector3(radial * Math.cos(angle) * r, radial * Math.sin(angle) * r, z * r);
  }

  private createVelocity(preset: Preset, position: THREE.Vector3, output: THREE.Vector3): void {
    if (preset.velocityMode === 'uniform') {
      output.set(0, preset.velocityY, preset.velocityX);
      return;
    }

    const radiusXY = Math.max(Math.hypot(position.x, position.y), 1);
    const radius3D = Math.max(position.length(), 1);
    if (preset.velocityMode === 'keplerian') {
      const speed = Math.sqrt(preset.gravity * 0.5 / radiusXY);
      output.set(-position.y / radiusXY * speed, position.x / radiusXY * speed, 0);
      return;
    }
    if (preset.velocityMode === 'tidalFlyby') {
      const localY = position.y + preset.offsetY;
      const localZ = position.z + preset.offsetX;
      output.set(preset.velocityX, -localZ * 0.0015, localY * 0.0015);
      return;
    }
    if (preset.velocityMode === 'cloudCollision') {
      const direction = position.x < 0 ? 1 : -1;
      output.set(direction * preset.velocityX, -position.z * 0.002, position.y * 0.002);
      return;
    }
    if (preset.velocityMode === 'binaryLagrange') {
      const angle = Math.atan2(position.y, position.x);
      const speed = Math.sqrt(preset.gravity / radiusXY) * (0.92 + Math.cos(angle * 2) * 0.06);
      output.set(-position.y / radiusXY * speed, position.x / radiusXY * speed, Math.sin(angle * 2) * speed * 0.035);
      return;
    }

    // Rotating collapse: sub-orbital spin plus a small inward component.
    const orbitalSpeed = Math.sqrt(preset.gravity * 0.5 / radius3D);
    const tangential = orbitalSpeed * 0.55;
    const inward = orbitalSpeed * 0.08;
    output.set(
      -position.y / radiusXY * tangential - position.x / radius3D * inward,
      position.x / radiusXY * tangential - position.y / radius3D * inward,
      -position.z / radius3D * inward
    );
  }

  private applyLegacyMotionTuning(
    preset: Preset,
    position: THREE.Vector3,
    velocity: THREE.Vector3,
    masses: THREE.Vector3[]
  ): void {
    for (const mass of masses) {
      const dx = mass.x - position.x;
      const dy = mass.y - position.y;
      const dz = mass.z - position.z;
      const distanceSquared = Math.max(dx * dx + dy * dy + dz * dz, 0.0001);
      const inverseDistance = 1 / Math.sqrt(distanceSquared);
      const scale = preset.gravity * 0.5 * inverseDistance / distanceSquared;
      velocity.x += dx * scale;
      velocity.y += dy * scale;
      velocity.z += dz * scale;
    }
  }

  private dataTexture(data: Float32Array): THREE.DataTexture {
    const texture = new THREE.DataTexture(data, this.size, this.size, THREE.RGBAFormat, THREE.FloatType);
    texture.minFilter = THREE.NearestFilter;
    texture.magFilter = THREE.NearestFilter;
    texture.wrapS = texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.generateMipmaps = false;
    texture.needsUpdate = true;
    return texture;
  }

  private target(): THREE.WebGLRenderTarget {
    return new THREE.WebGLRenderTarget(this.size, this.size, {
      type: THREE.FloatType,
      format: THREE.RGBAFormat,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      wrapS: THREE.ClampToEdgeWrapping,
      wrapT: THREE.ClampToEdgeWrapping,
      depthBuffer: false,
      stencilBuffer: false,
      count: 2
    });
  }

  private disposeState(): void {
    if (this.points) {
      this.root.remove(this.points);
      this.points.geometry.dispose();
      this.points = undefined;
    }
    if (this.trails) {
      this.root.remove(this.trails);
      this.trails.geometry.dispose();
      const material = this.trails.material;
      if (Array.isArray(material)) material.forEach((item) => item.dispose());
      else material.dispose();
      this.trails = undefined;
    }
    this.stateTargets.forEach((target) => target.dispose());
    this.trailTargets.forEach((target) => target.dispose());
    this.initialTextures.forEach((texture) => texture.dispose());
    this.stateTargets = [];
    this.trailTargets = [];
    this.initialTextures = [];
  }
}
